import { FaRegFileAlt } from 'react-icons/fa';
import { useState, useEffect } from 'react';
import { HiDownload, HiDocumentReport, HiEye } from 'react-icons/hi';
import { generateReport } from '../../components/services/ReportService';
import { useTenantSelection } from '../../providers/TenantSelectionProvider';
import { useSignalR } from '../../providers/SignalRProvider/SignalRProvider';
import { useToast } from '../../providers/ToastProvider/ToastContext';
import { getCustomerName } from '../../components/services/TenantSettingsService';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';

export const reports = [
  {
    title: 'Executive Report',
    description:
      'A comprehensive summary of patch activity, device compliance, reboots, and the top required updates across your environment for the last 30 days.',
    type: 'executivereport',
  },
  {
    title: 'Daily Update Summary',
    description:
      'The report displays the number of installed updates in the last 24 hours and the number of currently missing installations.',
    type: 'dailyupdatesummary',
  },
  {
    title: 'Installed Windows Updates',
    description: 'All Windows updates installed on the managed endpoints.',
    type: 'installedwindowsupdates',
  },
  {
    title: 'Missing Third-Party & Windows Updates',
    description: 'Shows the list of all missing third-party and windows updates on multiple remote computers grouped by update name.',
    type: 'missingthirdpartyandwindowsupdates',
  },
  {
    title: 'Missing Windows Critical Updates',
    description: 'Remotely view list of all missing security updates and patches on multiple remote computers at once.',
    type: 'missingwindowscriticalupdates',
  },
  {
    title: 'Missing Windows Updates',
    description: 'Remotely view list of all missing security updates and patches on multiple remote computers at once.',
    type: 'missingwindowsupdates',
  },
  {
    title: 'Reboot Required',
    description: 'Computers that require a reboot in order to complete updates, software deployment or other maintenance operations.',
    type: 'rebootrequired',
  },
  {
    title: 'Weekly Update Summary',
    description:
      'The report displays the number of installed updates in the last 7 days and the number of currently missing installations.',
    type: 'weeklyupdatesummary',
  },
  {
    title: 'Severity Report',
    description:
      'This report lists agents with outdated software by severity, showing their current and target versions, application, CVE score, and category.',
    type: 'severity',
  },
];

const ReportView = () => {
  const [loadingReports, setLoadingReports] = useState<Record<number, boolean>>({});
  const [downloadUrls, setDownloadUrls] = useState<Record<number, string>>({});
  const [groups, setGroups] = useState<any[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<string>('No Group');
  const [customerName, setCustomerName] = useState<string>('');
  const navigate = useNavigate();

  const { selectedTenantId } = useTenantSelection();
  const { isConnected, correlationId, registerListener, unregisterListener, ensureRegistered, connectNow } = useSignalR();
  const { addToast } = useToast();
  const { canEdit } = usePermissions();
  const canGenerateReport = canEdit('report');
  const userName = localStorage.getItem('userName') || 'Unknown User';

  // Load groupsRes and customerName from localStorage/api on mount
  useEffect(() => {
    const groupsRes = localStorage.getItem('groupsRes');
    if (groupsRes) {
      try {
        const parsed = JSON.parse(groupsRes);
        // Expecting groupsRes to be an object with .data[0].tenantGroupList
        const groupList = parsed?.data?.[0]?.tenantGroupList || [];
        setGroups(groupList);
      } catch (e) {
        // Invalid JSON, ignore
      }
    }
    // Load customer name
    if (selectedTenantId) {
      getCustomerName(selectedTenantId as string).then((name) => {
        setCustomerName(name || '');
      });
    }
  }, [selectedTenantId]);

  const handleViewReport = (report: (typeof reports)[0]) => {
    let endpoint = `/Report/Download?reportType=${report.type}`;
    if (selectedGroup !== 'No Group') {
      endpoint += `&tenantGroupId=${encodeURIComponent(selectedGroup)}`;
    }
    navigate(endpoint);
  };
  // Register SignalR listener for report completion
  useEffect(() => {
    const listener = (job: any) => {
      console.log('job message:', job.message);

      const firstWord = job.message?.split(' ')[0]?.toLowerCase();
      const reportIdx = reports.findIndex((r) => r.type.toLowerCase() === firstWord.toLowerCase());
      const reportType = reportIdx !== -1 ? reports[reportIdx].type : 'report';

      if (reportIdx !== -1) {
        setLoadingReports((prev) => ({ ...prev, [reportIdx]: false }));
        if (job.isSuccessful && job.result) {
          setDownloadUrls((prev) => ({ ...prev, [reportIdx]: job.result }));
          setTimeout(() => {
            setDownloadUrls((prev) => {
              const updated = { ...prev };
              delete updated[reportIdx];
              return updated;
            });
          }, 60000);
        }
      }

      addToast(
        job.message,
        job.isSuccessful ? 'success' : 'error',
        5000,
        job.isSuccessful ? 'Report Update' : 'Report Error',
        false,
        job.result ? { downloadUrl: job.result, reportType } : undefined, // Pass reportType here
        job.correlationId,
        true,
      );
    };

    registerListener(listener);

    return () => {
      if (window.__sharedSignalRListeners) {
        window.__sharedSignalRListeners.delete(listener);
      }
      unregisterListener();
    };
  }, [registerListener, unregisterListener, addToast]);

  const handleGenerateReport = async (report: (typeof reports)[0], idx: number) => {
    setLoadingReports((prev) => ({ ...prev, [idx]: true }));

    if (!isConnected) {
      try {
        await connectNow();
      } catch {
        setLoadingReports((prev) => ({ ...prev, [idx]: false }));
        addToast('Unable to connect for live status updates. Please try again in a moment.', 'error', 4000, 'Connection Error');
        return;
      }
    }

    // Find selected group object
    let tenantGroupId: string | undefined = undefined;
    let tenantGroupName: string | undefined = undefined;
    if (selectedGroup !== 'No Group') {
      const groupObj = groups.find((g: any) => g.groupName === selectedGroup);
      if (groupObj) {
        tenantGroupId = groupObj.tenantGroupId;
        tenantGroupName = groupObj.groupName;
      }
    }

    try {
      const payload: any = {
        reportType: report.type,
        tenantId: selectedTenantId || '',
        tenantName: customerName || '',
        correlationId: correlationId,
        requestedAtUtc: new Date().toISOString(),
      };
      if (tenantGroupId) payload.tenantGroupId = tenantGroupId;
      if (tenantGroupName) payload.tenantGroupName = tenantGroupName;

      // Re-confirm the server has this tab's correlationId mapped to a live SignalR connection
      // right before requesting the report. `isConnected` can be true while the server-side
      // registration from connect/reconnect is still in flight, which otherwise causes a
      // "No active client connection found" response.
      try {
        await ensureRegistered();
      } catch (err) {
        console.warn('ensureRegistered failed before generating report', err);
      }

      let response = await generateReport(payload);

      if (response?.message?.includes('No active client connection')) {
        await ensureRegistered();
        response = await generateReport(payload);
      }

      // Check the response status and show the appropriate toast
      if (response?.isSuccess && response?.statusCode === 'Accepted') {
        addToast(response?.message || 'Report generation started! You will be notified when ready.', 'info', 4000, 'Export Started');
      } else if (response?.isSuccess && response?.data) {
        addToast(response?.message || 'Report is already available! Click here to download.', 'success', 4000, 'Export Ready');
      } else {
        addToast(response?.message || 'Failed to start report generation', 'error', 3000, 'Error');
      }
    } catch (error) {
      setLoadingReports((prev) => ({ ...prev, [idx]: false }));
      addToast('Failed to start report generation', 'error', 3000, 'Error');
      console.error('Error generating report:', error);
    }
  };

  const downloadReport = (url: string, reportType: string) => {
    // Dispatch event to global handler
    window.dispatchEvent(new CustomEvent('performDownload', { detail: { url, reportType } }));
  };

  return (
    <div className="min-h-screen  p-6">
      <div className=" mx-auto">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            {/* Left side - Title */}
            <div className="flex items-center gap-3">
              {/* <div className="flex items-center gap-3">
                <div className="w-1 h-8 bg-gradient-to-b from-red-500 to-red-600 rounded-full"></div>
                <h1 className="text-3xl font-bold text-white tracking-tight">Reports</h1>
              </div> */}
            </div>

            {/* Right side - Group Dropdown */}
            <div className="flex items-center gap-3">
              <label className="text-sm text-gray-400 font-medium"></label>
              <div className="relative">
                <select
                  className="appearance-none bg-[#2a2a2a] text-white pl-4 pr-10 py-2.5 rounded-lg border border-[#444] hover:border-red-500/50 focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/20 transition-all duration-200 cursor-pointer min-w-[180px] shadow-lg [&>option]:bg-[#2a2a2a] [&>option]:text-white"
                  value={selectedGroup}
                  onChange={(e) => setSelectedGroup(e.target.value)}
                >
                  <option value="No Group" className="bg-[#2a2a2a] text-white">
                    All Groups
                  </option>
                  {groups.map((g: any) => (
                    <option key={g.tenantGroupId} value={g.groupName} className="bg-[#2a2a2a] text-white">
                      {g.groupName}
                    </option>
                  ))}
                </select>
                {/* Custom dropdown arrow */}
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
          <p className="text-gray-400 text-sm">Generate comprehensive system reports and analytics</p>
        </div>

        {/* Reports Grid - 3 columns */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {reports.map((report, idx) => (
            <div
              key={idx}
              className="group relative bg-[#1D2123] backdrop-blur-sm border border-[#3a3a3a] hover:border-red-500/60 rounded-xl p-6 transition-all duration-300 hover:shadow-2xl hover:shadow-red-500/10 hover:-translate-y-1 flex flex-col"
            >
              {/* Decorative corner accent */}
              <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-red-500/5 to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

              <div className="relative flex flex-col flex-1">
                {/* Header - Fixed height area */}
                <div className="mb-4">
                  <h2 className="font-bold text-white text-base mb-2 group-hover:text-white/90 transition-colors">{report.title}</h2>
                  <div className="min-h-[60px]">
                    {report.description && <p className="text-gray-400 text-xs leading-relaxed line-clamp-3">{report.description}</p>}
                  </div>
                </div>

                {/* Actions Section - Always at bottom */}
                <div className="mt-auto">
                  <div className="flex flex-row gap-2">
                    {downloadUrls[idx] ? (
                      <button
                        onClick={() => downloadReport(downloadUrls[idx], report.type)}
                        className="flex-1 px-6 py-2.5 bg-gradient-to-r from-red-700 to-red-800 text-white text-sm font-semibold rounded-lg 
                        hover:from-red-700 hover:to-red-900 hover:shadow-lg hover:shadow-red-500/30 active:scale-95 
                        transition-all duration-200 flex items-center justify-center gap-2 whitespace-nowrap"
                      >
                        <HiDownload className="text-lg" />
                        <span>Download Report</span>
                      </button>
                    ) : (
                      <button
                        onClick={() => handleGenerateReport(report, idx)}
                        disabled={loadingReports[idx] || !canGenerateReport}
                        className="flex-1 px-6 py-2.5 bg-gradient-to-r from-red-700 to-red-800 text-white text-sm font-semibold rounded-lg 
                        hover:from-red-700 hover:to-red-900 hover:shadow-lg hover:shadow-red-500/30 active:scale-95 
                        disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none disabled:active:scale-100 disabled:from-red-600 disabled:to-red-700
                        transition-all duration-200 flex items-center justify-center gap-2 whitespace-nowrap"
                      >
                        {loadingReports[idx] ? (
                          <>
                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              />
                            </svg>
                            <span>Generating...</span>
                          </>
                        ) : (
                          <>
                            <HiDownload className="text-lg" />
                            <span>Generate Report</span>
                          </>
                        )}
                      </button>
                    )}
                    <button
                      onClick={() => {
                        let groupId = '';
                        if (selectedGroup !== 'No Group') {
                          const groupObj = groups.find((g: any) => g.groupName === selectedGroup);
                          if (groupObj) groupId = groupObj.tenantGroupId || '';
                        }
                        const params = new URLSearchParams();
                        if (groupId) params.append('tenantGroupId', groupId);
                        params.append('reportType', report.type);
                        navigate(`/Report/Download?${params.toString()}`);
                      }}
                      className="flex-1 px-6 py-2.5 text-white text-sm font-semibold rounded-xl border border-red-800 bg-transparent hover:bg-red-800 transition-all duration-200 flex items-center justify-center gap-2 whitespace-nowrap"
                    >
                      <span>View</span>
                    </button>
                  </div>
                  {/* View Button */}
                  {/* <div className="mt-3">
                    <button
                      onClick={() => handleViewReport(report)}
                      className="w-full px-6 py-2.5 bg-gradient-to-r from-red-700 to-red-800 text-white text-sm font-semibold rounded-lg 
                      hover:from-red-700 hover:to-red-900 hover:shadow-lg hover:shadow-red-500/30 active:scale-95 
                      transition-all duration-200 flex items-center justify-center gap-2 whitespace-nowrap"
                    >
                      <HiEye className="text-lg" />
                      <span>View Report</span>
                    </button>
                  </div> */}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ReportView;
